import React, { useState, useCallback, useRef, useContext, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { AppBar, Box, Grid, Paper, Toolbar, Typography, TextField, Button, Divider, Hidden, IconButton, Chip, Snackbar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import SvgIcon from '@material-ui/core/SvgIcon';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import DescriptionIcon from '@material-ui/icons/Description';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import LinkIcon from '@material-ui/icons/Link';

import SpeechTextArea from './SpeechTextArea';
import ScoreSlider from './ScoreSlider';
import StatusBar from './StatusBar';
import EmailModal from './EmailModal';
import EvaluationReport from './EvaluationReport';
import EvaluationTextReport from './EvaluationTextReport';
import ProspectTypeahead from './ProspectTypeahead';
import NetworkStatusBar from './NetworkStatusBar';
// import SyncPanel from './SyncPanel'; // OFFLINE SYNC DISABLED

import { startSpeechRecognition, stopSpeechRecognition } from '../../../services/speech.service';
import { AppDataContext } from '../../../common/AppContext';
import { EvaluationCategoryData } from '../../../services';
import Loader from '../../../sharedComponents/Loader/Loader';
import { useLocation, useHistory } from 'react-router-dom';
import RouteConstants from '../../../common/RouteConstants';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AddNewEvent from '../../master/evaluationEvents/AddNewEvent';
import AddProspectModal from './AddProspectModal';
import Helper from '../../../common/Helper';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { processSyncQueue, subscribeSyncEvents, transcribeAllRecordings } from '../../../services/sync.service';
import {
  getDraftKey, saveDraft, loadDraft, deleteDraft,
  addOrUpdateSyncQueueItem, addOrUpdateProspectSyncQueueItem, getPendingSyncCount,
  removeSyncQueueItemsByDraftKey,
  saveAudioRecording, getAudioRecordingsForField, deleteAudioRecording,
  cacheReferenceData, getCachedReferenceData,
  getUntranscribedRecordings, getAllDrafts, createLocalProspectUID,
  updateQueuedEvaluationTextFromDraft,
  isValidUID, sanitizeUID,
} from '../../../services/offlineStorage.service';

// ── SparklesIcon (defined after imports) ──
const SparklesIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M9.5 2l1.5 4.5L15.5 8l-4.5 1.5L9.5 14l-1.5-4.5L3.5 8l4.5-1.5L9.5 2z" />
    <path d="M18 12l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z" />
  </SvgIcon>
);

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: '#fff',
    color: 'red',
    top: theme.mixins.toolbar.minHeight,
    zIndex: theme.zIndex.appBar,
  },
  title: {
    flexGrow: 1,
  },
  content: {
    padding: theme.spacing(3, 2),
    marginTop: theme.mixins.toolbar.minHeight,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(3),
      marginTop: theme.mixins.toolbar.minHeight,
    },
    overflowY: 'auto',
    height: `calc(100vh - ${theme.mixins.toolbar.minHeight * 2}px)`,
  },
  dateField: {
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      cursor: 'pointer',
    },
  },
  '@global': {
    '.spin-icon': {
      animation: '$spin 1s linear infinite',
    },
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  },
}));

const getTodayDate = () => Helper.FormatDate(new Date());

// const DEFAULT_SYSTEM_PROMPT = `You are an expert NBA officiating scout. You are provided with a prospect evaluation that includes notes and ratings (1-5 scale) across multiple categories: Communication, Court Presence, Fitness, Mechanics, and Play Calling.
//
// Your task is to write a professional, concise evaluation summary (3-4 points) that:
// 1. Opens with an overall assessment of the prospect's performance
// 2. Highlights key strengths (categories/subcategories rated 4-5)
// 3. Identifies areas for development (categories/subcategories rated 1-2)
// 4. References specific observations from the evaluator's notes when available
// 5. Closes with actionable recommendations for the prospect's growth
//
// Use professional sports officiating language. Be constructive and specific.
//
// Keep the tone professional but encouraging.`;
const DEFAULT_SYSTEM_PROMPT = '';

const initialFormState = {
  id: null,
  refereeName: '',
  evaluationDate: getTodayDate(),
  // Evaluation summary (speech-to-text)
  evaluationSummary: '',
  status: 'Draft',
};

const isDraftRecoverable = (draft) => (
  !!draft
  && !!draft._draftKey
  && !!draft.form
  && !draft.form.id
  && !!draft.selectedProspectUID
  && !!draft.selectedEvent?.id
);

const EvaluationForm = ({ evaluation, onSaved, onCancel }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [form, setForm] = useState(evaluation || initialFormState);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [interimText, setInterimText] = useState('');
  const [status, setStatus] = useState({ message: '', type: '' });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [isSaving, setIsSaving] = useState(false);
  // Synchronous guard to prevent a second save/submit from starting before
  // React re-renders the disabled button (e.g. rapid double-clicks).
  const isSavingRef = useRef(false);
  const [isSyncingData, setIsSyncingData] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summarizingCategoryId, setSummarizingCategoryId] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showTextReport, setShowTextReport] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const recognizedTextRef = useRef('');
  const { evaluationEvents, getUpdatedEvaluationEvents, addSnackMessages, user } = useContext(AppDataContext);
  const [evalUserList, setEvalUserList] = useState([]);
  const [evaluationCategories, setEvaluationCategories] = useState([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(location.state?.evaluationEvent || null);
  const [openAddEvent, setOpenAddEvent] = useState(false);
  const [scoutingCategoryList, setScoutingCategoryList] = useState([]);
  const [isScoutingCategoriesLoading, setIsScoutingCategoriesLoading] = useState(true);
  const [openAddProspect, setOpenAddProspect] = useState(false);
  const [competencies, setCompetencies] = useState([]);
  const [selectedCompetencies, setSelectedCompetencies] = useState({});
  const [tangibles, setTangibles] = useState([]);
  const [selectedTangibles, setSelectedTangibles] = useState([]);
  const [selectedProspectUID, setSelectedProspectUID] = useState('');
  const pendingEventNameRef = useRef(null);

  // ── Draft Detection and Loading ──
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [existingDraftData, setExistingDraftData] = useState(null);
  const [draftProspectName, setDraftProspectName] = useState('');
  const draftDetectionSkippedRef = useRef(false);
  const latestDraftPromptedRef = useRef(false);
  // Tracks the original localStorage key of the draft that was detected/loaded,
  // so it can be cleaned up on a successful online save even if the recomputed
  // draft key differs (e.g. offline-created `new_<timestamp>` keys).
  const loadedDraftKeyRef = useRef(null);

  // ── Offline / Network Status ──
  // OFFLINE SYNC DISABLED - Evaluation form works only in online mode
  const [isOfflineMode, setIsOfflineMode] = useState(!navigator.onLine);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [pendingSyncCount, setPendingSyncCount] = useState(getPendingSyncCount());
  const [pendingAudioCount, setPendingAudioCount] = useState(0);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [audioRecordingsMap, setAudioRecordingsMap] = useState({}); // { fieldName: [recordings] }
  const [draftKey, setDraftKey] = useState(null);
  const [showOfflineSubmitDialog, setShowOfflineSubmitDialog] = useState(false);

  // ── Summarization Confirmation Dialog ──
  const [showSummarizationDialog, setShowSummarizationDialog] = useState(false);
  const [pendingSummarization, setPendingSummarization] = useState(null); // { fieldName, dialogTitle, sourceLabel, summarizedText, originalText, successMessage, discardMessage }

  useEffect(() => {
    const loadSystemPrompt = async () => {
      try {
        const prompt = await EvaluationCategoryData.getSystemPromptByKey('EVALUATION_SUMMARY');
        if (prompt && typeof prompt === 'string') {
          setSystemPrompt(prompt);
        }
      } catch (error) {
        console.warn('Failed to load system prompt from DB:', error);
      }
    };

    loadSystemPrompt();
  }, []);

  // ── Draft Key Management ──
  useEffect(() => {
    if (selectedProspectUID && selectedEvent?.id) {
      setDraftKey(getDraftKey(selectedProspectUID, selectedEvent.id));
    } else {
      setDraftKey(null);
    }
  }, [selectedProspectUID, selectedEvent]);

  // ── Auto-draft on first event + prospect selection (new forms only) ──
  // useEffect(() => {
  //   if (!draftKey || form.id) return;
  //   const existing = loadDraft(draftKey);
  //   if (!existing) {
  //     // Both event and prospect selected for the first time — save an initial draft immediately
  //     saveDraft(draftKey, { form, selectedCompetencies, selectedTangibles, selectedProspectUID, selectedEvent });
  //     setLastAutoSave(Date.now());
  //     setToast({ open: true, message: 'Draft started. Your progress will be saved automatically.', severity: 'info' });
  //   }
  // }, [draftKey]);

  // ── Offer recovery of the most recent draft when reopening a blank new form ──
  useEffect(() => {
    if (latestDraftPromptedRef.current || location.state?.evaluation || form.id) return;
    if (selectedProspectUID || selectedEvent?.id) return;

    const latestDraft = getAllDrafts()
      .filter(isDraftRecoverable)
      .sort((left, right) => (right._savedAt || 0) - (left._savedAt || 0))[0];

    if (!latestDraft) return;

    latestDraftPromptedRef.current = true;
    loadedDraftKeyRef.current = latestDraft._draftKey || null;
    setExistingDraftData(latestDraft);
    setDraftProspectName(latestDraft.form?.refereeName || 'this prospect');
    setShowDraftDialog(true);
  }, [form.id, location.state?.evaluation, selectedEvent, selectedProspectUID]);

  // ── Detect Existing Draft and Prompt Loading ──
  useEffect(() => {
    if (!selectedProspectUID || !selectedEvent?.id) {
      draftDetectionSkippedRef.current = false;
      return;
    }

    // Don't prompt while an offline sync is in progress. Syncing swaps a
    // prospect's temporary local UID for its server UID and relocates the draft
    // to the new key; without this guard that mid-sync change re-triggers the
    // "Found Saved Draft" prompt even though the user never navigated.
    if (isSyncingData) {
      return;
    }

    // Mark this draft key as handled so we don't prompt again in this session
    const currentDraftKey = getDraftKey(selectedProspectUID, selectedEvent.id);
    if (draftDetectionSkippedRef.current === currentDraftKey) {
      return;
    }

    draftDetectionSkippedRef.current = currentDraftKey;

    const existing = loadDraft(currentDraftKey);
    if (isDraftRecoverable(existing)) {
      loadedDraftKeyRef.current = existing._draftKey || currentDraftKey;
      setExistingDraftData(existing);
      setDraftProspectName(existing.form?.refereeName || 'this prospect');
      setShowDraftDialog(true);
    }
  }, [selectedProspectUID, selectedEvent, location.state?.evaluation, isSyncingData]);

  // ── Auto-Save Hook ──
  const autoSaveData = useMemo(() => ({
    form,
    selectedCompetencies,
    selectedTangibles,
    selectedProspectUID,
    selectedEvent,
  }), [form, selectedCompetencies, selectedTangibles, selectedProspectUID, selectedEvent]);

  useAutoSave(draftKey, autoSaveData, (timestamp, savedData, trigger) => {
    if (trigger !== 'unmount') {
      setLastAutoSave(timestamp);
    }

    if (!navigator.onLine || isOfflineMode) {
      queueDraftSnapshotForSync(savedData, {
        skipStateUpdates: trigger === 'unmount',
      });
    }
  }, { enabled: !navigator.onLine || isOfflineMode });

  // ── Update Pending Counts ──
  // OFFLINE SYNC DISABLED
  // useEffect(() => {
  //   setPendingSyncCount(getPendingSyncCount());
  //   getUntranscribedRecordings().then((recs) => setPendingAudioCount(recs.length));
  // }, [showSyncPanel]);

  // ── Browser Online/Offline Events ──
  useEffect(() => {
    const handleOnline = () => {
      setIsOfflineMode(false);
      const pendingCount = getPendingSyncCount();
      setPendingSyncCount(pendingCount);
      getUntranscribedRecordings().then((recs) => {
        setPendingAudioCount(recs.length);
        const total = pendingCount + recs.length;
        if (total > 0) {
          const parts = [];
          if (pendingCount > 0) parts.push(`${pendingCount} draft${pendingCount !== 1 ? 's' : ''}`);
          if (recs.length > 0) parts.push(`${recs.length} audio recording${recs.length !== 1 ? 's' : ''}`);
          setToast({
            open: true,
            message: `You're back online! ${parts.join(' and ')} saved locally and ready to sync.`,
            severity: 'info',
          });
        }
      });
    };
    const handleOffline = () => {
      setIsOfflineMode(true);
      setPendingSyncCount(getPendingSyncCount());
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeSyncEvents((event) => {
      if (!event || event.type !== 'item-synced') {
        return;
      }

      const syncedItem = event.data?.item;
      if (!syncedItem || typeof syncedItem !== 'object') {
        return;
      }

      const rawLocalProspectUID = typeof syncedItem.localProspectUID === 'string'
        ? syncedItem.localProspectUID
        : '';
      const rawSyncedOfficialUID = typeof syncedItem.syncedOfficialUID === 'string'
        ? syncedItem.syncedOfficialUID
        : '';

      // SECURITY: Validate untrusted external data before using it
      // Prevent injection attacks via external sync events
      if (!rawLocalProspectUID || !rawSyncedOfficialUID) {
        return;
      }

      const localProspectUID = sanitizeUID(rawLocalProspectUID);
      const syncedOfficialUID = sanitizeUID(rawSyncedOfficialUID);

      // Reject if sanitization changes input payload from external source.
      if (
        !localProspectUID
        || !syncedOfficialUID
        || localProspectUID !== rawLocalProspectUID
        || syncedOfficialUID !== rawSyncedOfficialUID
      ) {
        console.warn('Invalid sync event UID payload rejected', {
          localProspectUID: rawLocalProspectUID,
          syncedOfficialUID: rawSyncedOfficialUID,
        });
        return;
      }

      // Validate UIDs are in safe format (alphanumeric, hyphens, underscores, dots)
      if (!isValidUID(localProspectUID)) {
        console.warn('Invalid localProspectUID received from sync event:', { localProspectUID });
        return;
      }

      if (!isValidUID(syncedOfficialUID)) {
        console.warn('Invalid syncedOfficialUID received from sync event:', { syncedOfficialUID });
        return;
      }

      // Validate that the sync event is for the currently selected prospect
      if (localProspectUID !== selectedProspectUID) {
        return;
      }

      setSelectedProspectUID(syncedOfficialUID);
      setEvalUserList((prev) => prev.map((prospect) => (
        prospect.id === localProspectUID
          ? { ...prospect, id: syncedOfficialUID, officialUID: syncedOfficialUID, OfficialUID: syncedOfficialUID }
          : prospect
      )));

      if (selectedEvent?.id) {
        const nextDraftKey = getDraftKey(syncedOfficialUID, selectedEvent.id);
        draftDetectionSkippedRef.current = nextDraftKey;
        setDraftKey(nextDraftKey);
        // Keep the loaded-draft reference pointing at the relocated key so the
        // post-sync reset check can still recognise the currently loaded draft.
        loadedDraftKeyRef.current = nextDraftKey;
      }
    });

    return () => {
      unsubscribe();
    };
  }, [selectedEvent, selectedProspectUID]);

  // Hydrate form from a saved evaluation passed via navigation state
  useEffect(() => {
    const navEval = location.state?.evaluation;
    if (navEval) {
      const hydratedForm = {
        ...initialFormState,
        id: navEval.evaluationID,
        refereeName: `${navEval.firstName || ''} ${navEval.lastName || ''}`.trim(),
        evaluationDate: navEval.evaluationDate
          ? Helper.FormatDate(navEval.evaluationDate)
          : getTodayDate(),
        evaluationSummary: navEval.comments || '',
        status: navEval.isDraft ? 'Draft' : 'Submitted',
      };
      (navEval.evaluationRatingList || []).forEach((r) => {
        if (r.subCategoryID && r.subCategoryID > 0) {
          hydratedForm[`sub_${r.subCategoryID}`] = r.rating;
        } else {
          hydratedForm[`cat_${r.categoryID}`] = r.rating;
        }
      });
      (navEval.commentsList || []).forEach((c) => {
        hydratedForm[`category_${c.categoryID}`] = c.comment;
      });
      setForm(hydratedForm);
      setSelectedProspectUID(navEval.officialUID || '');

      // Hydrate competencies
      const compMap = {};
      (navEval.evaluationRatingCompetencyList || []).forEach((rc) => {
        const key = rc.subCategoryID > 0
          ? `sub_comp_${rc.subCategoryID}`
          : `cat_comp_${rc.categoryID}`;
        if (!compMap[key]) compMap[key] = [];
        if (!compMap[key].includes(rc.competencyID)) {
          compMap[key].push(rc.competencyID);
        }
      });
      setSelectedCompetencies(compMap);

      // Hydrate tangibles
      setSelectedTangibles(navEval.evaluationTangibles || []);
    }
  }, [location.state?.evaluation]);

  useEffect(() => {
    // Try online fetch first, fall back to cached reference data
    EvaluationCategoryData.getAllEvaluationCategoryList().then((result) => {
      setEvaluationCategories(result);
      cacheReferenceData('categories', result);
    }).catch(() => {
      const cached = getCachedReferenceData('categories');
      if (cached) setEvaluationCategories(cached);
    }).finally(() => {
      setIsCategoriesLoading(false);
    });
    EvaluationCategoryData.getCompetenciesList().then((result) => {
      const active = (result || []).filter((c) => c.competencyActive);
      setCompetencies(active);
      cacheReferenceData('competencies', active);
    }).catch(() => {
      const cached = getCachedReferenceData('competencies');
      if (cached) setCompetencies(cached);
    });
    EvaluationCategoryData.getTangibleList().then((result) => {
      const active = (result || []).filter((t) => t.isActive);
      setTangibles(active);
      cacheReferenceData('tangibles', active);
    }).catch(() => {
      const cached = getCachedReferenceData('tangibles');
      if (cached) setTangibles(cached);
    });
    EvaluationCategoryData.getScoutingCategoryMaster().then((result) => {
      setScoutingCategoryList(Array.isArray(result) ? result.filter((c) => c.categoryActive) : []);
    }).catch(() => {
      setScoutingCategoryList([]);
    }).finally(() => {
      setIsScoutingCategoriesLoading(false);
    });
    getUpdatedEvaluationEvents();
  }, []);

  // Auto-select newly created event after events list refreshes
  useEffect(() => {
    if (pendingEventNameRef.current && evaluationEvents && evaluationEvents.length > 0) {
      const match = evaluationEvents.find(e => e.eventName === pendingEventNameRef.current);
      if (match) {
        setSelectedEvent(match);
        pendingEventNameRef.current = null;
      }
    }
  }, [evaluationEvents]);

  const toggleCompetency = (key, compId) => {
    setSelectedCompetencies((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(compId)
          ? current.filter((id) => id !== compId)
          : [...current, compId],
      };
    });
  };

  const toggleTangible = (tangibleId) => {
    setSelectedTangibles((prev) =>
      prev.includes(tangibleId)
        ? prev.filter((id) => id !== tangibleId)
        : [...prev, tangibleId]
    );
  };

  const searchProspects = useCallback((searchTerm) => {
    if (searchTerm && searchTerm.trim().length >= 3) {
      setEvalUserList([]);
      return EvaluationCategoryData.searchEvalUsers(searchTerm.trim()).then((result) => {
        setEvalUserList(result || []);
      });
    } else {
      setEvalUserList([]);
      return Promise.resolve();
    }
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleProspectSave = useCallback(async (prospectData) => {
    const payload = {
      email: prospectData.email?.trim() || '',
      firstName: prospectData.firstName?.trim() || '',
      lastName: prospectData.lastName?.trim() || '',
    };

    if (!navigator.onLine || isOfflineMode) {
      const localProspectUID = createLocalProspectUID();
      addOrUpdateProspectSyncQueueItem(payload, localProspectUID);
      setPendingSyncCount(getPendingSyncCount());
      setIsOfflineMode(true);
      setToast({
        open: true,
        message: 'You are offline. Prospect saved locally and queued for sync.',
        severity: 'warning',
      });
      return {
        ...payload,
        officialUID: localProspectUID,
      };
    }

    try {
      return await EvaluationCategoryData.insertEvalProspect(payload);
    } catch (error) {
      const localProspectUID = createLocalProspectUID();
      addOrUpdateProspectSyncQueueItem(payload, localProspectUID);
      setPendingSyncCount(getPendingSyncCount());
      setToast({
        open: true,
        message: 'Prospect could not be saved online. It was queued locally instead.',
        severity: 'warning',
      });
      return {
        ...payload,
        officialUID: localProspectUID,
      };
    }
  }, [isOfflineMode]);

  const handleMicClick = useCallback(async (fieldName) => {
    if (isListening && activeField === fieldName) {
      // Stop recording for this field
      stopSpeechRecognition();
      setIsListening(false);
      setActiveField(null);
      setInterimText('');
      recognizedTextRef.current = '';
      return;
    }

    if (isListening) {
      // Stop current recording and switch to new field
      stopSpeechRecognition();
      setIsListening(false);
      setInterimText('');
      recognizedTextRef.current = '';
      // Short delay before starting new recognition
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setIsLoading(true);
    setActiveField(fieldName);
    recognizedTextRef.current = form[fieldName] || '';

    const success = await startSpeechRecognition(
      // onRecognizing (interim results)
      (text) => {
        setInterimText(text);
      },
      // onRecognized (final results)
      (text) => {
        setInterimText('');
        setForm((prev) => {
          const existing = prev[fieldName] || '';
          const updated = existing ? existing + ' ' + text : text;
          recognizedTextRef.current = updated;
          return { ...prev, [fieldName]: updated };
        });
      },
      // onError
      (error) => {
        setStatus({ message: error, type: 'error' });
      },
      // onStopped
      () => {
        setIsListening(false);
        setActiveField(null);
        setInterimText('');
      }
    );

    setIsLoading(false);
    if (success) {
      setIsListening(true);
    }
  }, [isListening, activeField, form]);

  const handleStopAll = useCallback(() => {
    stopSpeechRecognition();
    setIsListening(false);
    setActiveField(null);
    setInterimText('');
    recognizedTextRef.current = '';
  }, []);

  const mergeTranscriptionsIntoForm = useCallback((baseForm, transcriptionResults, targetDraftKey) => {
    const draftTranscriptions = (transcriptionResults || []).filter((result) => (
      targetDraftKey
      && result.draftKey === targetDraftKey
      && result.fieldName
      && result.transcription
      && result.transcription.trim()
    ));

    if (draftTranscriptions.length === 0) {
      return { nextForm: baseForm, appliedCount: 0, affectedFields: [] };
    }

    const nextForm = { ...baseForm };
    draftTranscriptions.forEach((result) => {
      const existing = nextForm[result.fieldName] || '';
      const transcription = result.transcription.trim();
      nextForm[result.fieldName] = existing ? `${existing} ${transcription}` : transcription;
    });

    return {
      nextForm,
      appliedCount: draftTranscriptions.length,
      affectedFields: [...new Set(draftTranscriptions.map((result) => result.fieldName))],
    };
  }, []);

  const buildEvaluationPayload = useCallback((sourceForm = form, submitStatus = 'Draft', sourceData = {}) => {
    const workingForm = sourceForm || form;
    const sourceSelectedEvent = sourceData.selectedEvent ?? selectedEvent;
    const sourceSelectedProspectUID = sourceData.selectedProspectUID ?? selectedProspectUID;
    const sourceSelectedCompetencies = sourceData.selectedCompetencies ?? selectedCompetencies;
    const sourceSelectedTangibles = sourceData.selectedTangibles ?? selectedTangibles;

    if (!sourceSelectedEvent?.id || !sourceSelectedProspectUID || !workingForm?.evaluationDate || !workingForm?.refereeName?.trim()) {
      return null;
    }

    const evaluationID = workingForm.id || 0;
    const officialUID = sourceSelectedProspectUID;
    const scoutID = Helper.getOfficialId();
    const evaluationRatingList = [];
    const evaluationRatingCompetencyList = [];
    const commentsList = [];

    (evaluationCategories || []).filter(cat => cat.categoryActive).forEach(cat => {
      const activeSubCategories = (cat.subCategories || []).filter(sub => sub.subCategoryActive);

      if (activeSubCategories.length > 0) {
        activeSubCategories.forEach(sub => {
          const rating = workingForm[`sub_${sub.id}`] || 0;
          if (rating > 0) {
            evaluationRatingList.push({
              categoryID: cat.id,
              subCategoryID: sub.id,
              rating,
              officialUID,
              evaluationID,
            });
          }

          const subKey = `sub_comp_${sub.id}`;
          (sourceSelectedCompetencies[subKey] || []).forEach(compId => {
            const comp = competencies.find(c => c.id === compId);
            if (comp) {
              evaluationRatingCompetencyList.push({
                id: comp.id,
                categoryID: cat.id,
                categoryName: cat.categoryName,
                subCategoryID: sub.id,
                subCategoryName: sub.subCategoryName,
                competencyName: comp.competencyName,
                competencyActive: comp.competencyActive,
                competencyID: comp.id,
                officialUID,
                evaluationID,
              });
            }
          });
        });
      } else {
        const rating = workingForm[`cat_${cat.id}`] || 0;
        if (rating > 0) {
          evaluationRatingList.push({
            categoryID: cat.id,
            subCategoryID: 0,
            rating,
            officialUID,
            evaluationID,
          });
        }

        const catKey = `cat_comp_${cat.id}`;
        (sourceSelectedCompetencies[catKey] || []).forEach(compId => {
          const comp = competencies.find(c => c.id === compId);
          if (comp) {
            evaluationRatingCompetencyList.push({
              id: comp.id,
              categoryID: cat.id,
              categoryName: cat.categoryName,
              subCategoryID: 0,
              subCategoryName: '',
              competencyName: comp.competencyName,
              competencyActive: comp.competencyActive,
              competencyID: comp.id,
              officialUID,
              evaluationID,
            });
          }
        });
      }

      const comment = workingForm[`category_${cat.id}`] || '';
      if (comment.trim()) {
        commentsList.push({
          commentId: 0,
          evaluationID,
          categoryID: cat.id,
          comment: comment.trim(),
        });
      }
    });

    return {
      evaluationID,
      evaluationDate: workingForm.evaluationDate,
      gameType: sourceSelectedEvent.gameType || 0,
      country: sourceSelectedEvent.country || 0,
      state: sourceSelectedEvent.state || 0,
      stateOther: sourceSelectedEvent.stateOther || '',
      city: sourceSelectedEvent.city || '',
      eventID: sourceSelectedEvent.id || 0,
      officialUID,
      scoutID,
      comments: workingForm.evaluationSummary || '',
      evaluationRatings: { evaluationID, ratingCategory: [] },
      evaluationRatingList,
      evaluationRatingCompetencyList,
      commentsList,
      evaluationTangibles: sourceSelectedTangibles,
      isDraft: submitStatus === 'Draft',
      refereeName: workingForm.refereeName,
    };
  }, [competencies, evaluationCategories, form, selectedCompetencies, selectedEvent, selectedProspectUID, selectedTangibles]);

  const queueDraftSnapshotForSync = useCallback((snapshot, options = {}) => {
    const { skipStateUpdates = false, submitStatus = 'Draft' } = options;
    const payload = buildEvaluationPayload(snapshot?.form, submitStatus, snapshot);
    if (!payload) {
      return false;
    }

    const targetDraftKey = draftKey || getDraftKey(snapshot?.selectedProspectUID, snapshot?.selectedEvent?.id);
    addOrUpdateSyncQueueItem(payload, targetDraftKey);

    if (!skipStateUpdates) {
      setPendingSyncCount(getPendingSyncCount());
    }

    return true;
  }, [buildEvaluationPayload, draftKey]);

  // ── Clear any local draft + queued sync artifacts for the current evaluation ──
  // Called after a successful online save/submit so the locally stored draft
  // (and the "pending sync" badge) do not linger and re-trigger the recovery prompt.
  const clearLocalDraftArtifacts = useCallback(() => {
    const candidateKeys = [
      draftKey,
      loadedDraftKeyRef.current,
      (selectedProspectUID && selectedEvent?.id) ? getDraftKey(selectedProspectUID, selectedEvent.id) : null,
    ];

    const uniqueKeys = [...new Set(candidateKeys.filter(Boolean))];
    uniqueKeys.forEach((key) => {
      deleteDraft(key);
      removeSyncQueueItemsByDraftKey(key);
    });

    loadedDraftKeyRef.current = null;
    setPendingSyncCount(getPendingSyncCount());
  }, [draftKey, selectedProspectUID, selectedEvent]);

  const handleSave = async (submitStatus = 'Draft', options = {}) => {
    const { resetAfterQueue = false } = options;
    // Re-entrancy guard: ignore additional clicks while a save/submit is already running.
    if (isSavingRef.current) {
      return;
    }
    if (!selectedEvent) {
      setStatus({ message: 'Please select an evaluation event.', type: 'error' });
      return;
    }
    if (!form.evaluationDate) {
      setStatus({ message: 'Please enter the evaluation date.', type: 'error' });
      return;
    }
    if (!form.refereeName.trim()) {
      setStatus({ message: 'Please enter the prospect name.', type: 'error' });
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      let workingForm = form;

      // If we are online, flush any pending offline work first so the current
      // save/submit runs after the queue has been processed.
      if (!navigator.onLine) {
        setIsOfflineMode(true);
      } else {
        workingForm = await syncPendingWorkBeforeSave(workingForm, submitStatus);
      }

      const payload = buildEvaluationPayload(workingForm, submitStatus);
      if (!payload) {
        setStatus({ message: 'Please complete the event, prospect, and evaluation date before saving.', type: 'error' });
        setIsSaving(false);
        return;
      }

      // ── Offline Check: save locally if device has no internet connection ──
      if (!navigator.onLine) {
        const currentDraftKey = draftKey || getDraftKey(selectedProspectUID, selectedEvent?.id);
        addOrUpdateSyncQueueItem(payload, currentDraftKey);
        saveDraft(currentDraftKey, { form: workingForm, selectedCompetencies, selectedTangibles, selectedProspectUID, selectedEvent });
        setPendingSyncCount(getPendingSyncCount());
        setToast({
          open: true,
          message: submitStatus === 'Completed'
            ? 'You are offline. Evaluation was queued for submission and a new form is ready.'
            : 'You are offline. Draft saved locally and will sync when you are back online.',
          severity: 'warning',
        });
        if (submitStatus === 'Completed' && resetAfterQueue) {
          handleReset();
        }
        setIsSaving(false);
        return;
      }

      // ── Online Mode: API call only ──
      try {
        const saved = await EvaluationCategoryData.saveEvaluationRatings(payload);
        if (submitStatus === 'Completed') {
          const returnedId = typeof saved === 'number' ? saved
            : (saved?.evaluationID ?? saved?.id ?? saved?.data?.evaluationID ?? saved?.data?.id ?? null);
          if (returnedId) {
            setForm((prev) => ({ ...prev, id: returnedId, status: 'Submitted' }));
          } else {
            setForm((prev) => ({ ...prev, status: 'Submitted' }));
          }
          setToast({ open: true, message: 'Evaluation submitted successfully!', severity: 'success' });
          // Clean up local draft on successful submit
          clearLocalDraftArtifacts();
        } else {
          const returnedId = typeof saved === 'number' ? saved
            : (saved?.evaluationID ?? saved?.id ?? saved?.data?.evaluationID ?? saved?.data?.id ?? null);
          if (returnedId) {
            setForm((prev) => ({ ...prev, id: returnedId }));
          }
          setToast({ open: true, message: 'Evaluation saved as draft.', severity: 'success' });
          // Clean up any local/offline draft now that it lives on the server.
          clearLocalDraftArtifacts();
        }
        if (onSaved) onSaved(saved);
      } catch (apiError) {
        // OFFLINE SYNC DISABLED - No offline fallback
        // API call failed — fall back to local save
        console.warn('API save failed, falling back to local save:', apiError);
        const currentDraftKey = draftKey || getDraftKey(selectedProspectUID, selectedEvent?.id);
        addOrUpdateSyncQueueItem(payload, currentDraftKey);
        saveDraft(currentDraftKey, { form: workingForm, selectedCompetencies, selectedTangibles, selectedProspectUID, selectedEvent });
        setPendingSyncCount(getPendingSyncCount());
        setToast({
          open: true,
          message: submitStatus === 'Completed'
            ? 'Network unavailable. Evaluation was queued for submission and a new form is ready.'
            : 'Network unavailable — saved locally instead. Will sync when online.',
          severity: 'warning',
        });
        if (submitStatus === 'Completed' && resetAfterQueue) {
          handleReset();
        }
      }
    } catch (error) {
      setStatus({
        message: `Failed to save evaluation: ${error.response?.data?.error || error.message}`,
        type: 'error',
      });
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // ── Offline Audio Recording Handler ──
  const handleRecordingComplete = useCallback(async (fieldName, blob, duration) => {
    const currentDraftKey = draftKey || `eval_draft_temp_${Date.now()}`;
    try {
      await saveAudioRecording(currentDraftKey, fieldName, blob, duration);
      // Refresh recordings for this field so the badge appears immediately
      const recs = await getAudioRecordingsForField(currentDraftKey, fieldName);
      setAudioRecordingsMap((prev) => ({ ...prev, [fieldName]: recs }));
      getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
      setToast({ open: true, message: 'Recording saved — will transcribe when back online.', severity: 'info' });
    } catch (err) {
      console.error('Failed to save recording:', err);
      setToast({ open: true, message: 'Failed to save recording. Please try again.', severity: 'error' });
    }
  }, [draftKey]);

  const handleDeleteRecording = useCallback(async (recordId) => {
    try {
      await deleteAudioRecording(recordId);
      if (draftKey) {
        const allFields = Object.keys(audioRecordingsMap);
        const newMap = { ...audioRecordingsMap };
        for (const field of allFields) {
          newMap[field] = await getAudioRecordingsForField(draftKey, field);
        }
        setAudioRecordingsMap(newMap);
      }
      getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
    } catch (err) {
      console.error('Failed to delete recording:', err);
    }
  }, [draftKey, audioRecordingsMap]);

  // Load audio recordings from IndexedDB whenever the draft key or categories change
  useEffect(() => {
    if (!draftKey) return;
    const loadRecordings = async () => {
      const fields = (evaluationCategories || [])
        .filter((c) => c.categoryActive)
        .map((c) => `category_${c.id}`)
        .concat(['evaluationSummary']);
      const map = {};
      for (const field of fields) {
        map[field] = await getAudioRecordingsForField(draftKey, field);
      }
      setAudioRecordingsMap(map);
      getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
    };
    loadRecordings();
  }, [draftKey, evaluationCategories]);

  // Handle sync panel completion - apply transcriptions to form fields
  const handleTranscriptionComplete = useCallback(async (transcriptionResults, options = {}) => {
    const {
      baseForm = form,
      showNoAudioToast = true,
      showSuccessToast = true,
    } = options;

    // Filter out results with actual transcriptions and apply them to the form
    const successfulTranscriptions = transcriptionResults.filter(r => r.transcription && r.transcription.trim());

    if (successfulTranscriptions.length === 0) {
      if (showNoAudioToast) {
        setToast({
          open: true,
          message: 'No audio was successfully transcribed.',
          severity: 'info',
        });
      }
      getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
      return baseForm;
    }

    const successfulByDraftKey = successfulTranscriptions.reduce((groups, result) => {
      if (!result.draftKey) return groups;
      if (!groups[result.draftKey]) {
        groups[result.draftKey] = [];
      }
      groups[result.draftKey].push(result);
      return groups;
    }, {});

    Object.entries(successfulByDraftKey).forEach(([targetDraftKey, results]) => {
      const existingDraft = loadDraft(targetDraftKey);
      if (!existingDraft?.form) return;

      const { nextForm } = mergeTranscriptionsIntoForm(existingDraft.form, results, targetDraftKey);
      const nextDraft = {
        ...existingDraft,
        form: nextForm,
      };
      saveDraft(targetDraftKey, nextDraft);
      updateQueuedEvaluationTextFromDraft(targetDraftKey, nextDraft);
    });

    const currentDraftKey = draftKey || null;
    const {
      nextForm,
      appliedCount,
      affectedFields,
    } = mergeTranscriptionsIntoForm(baseForm, successfulTranscriptions, currentDraftKey);

    if (appliedCount > 0) {
      setForm(nextForm);
    }

    // Remove synced recordings from IndexedDB and from the UI.
    await Promise.all(successfulTranscriptions.map((r) => deleteAudioRecording(r.recordId)));
    if (affectedFields.length > 0) {
      setAudioRecordingsMap((prev) => {
        const next = { ...prev };
        affectedFields.forEach((field) => {
          next[field] = (prev[field] || []).filter(
            (rec) => !successfulTranscriptions.some((s) => s.recordId === rec.id)
          );
        });
        return next;
      });
    }

    if (appliedCount > 0 && showSuccessToast) {
      setToast({
        open: true,
        message: `Successfully transcribed ${appliedCount} audio recording${appliedCount !== 1 ? 's' : ''}!`,
        severity: 'success',
      });
    }

    getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
    return nextForm;
  }, [draftKey, form, mergeTranscriptionsIntoForm]);

  const handleSyncPending = useCallback(async () => {
    if (!navigator.onLine || isSyncingData) return;

    setIsSyncingData(true);
    try {
      // 1. Transcribe any saved audio recordings and apply to text fields
      const pendingRecs = await getUntranscribedRecordings();
      if (pendingRecs.length > 0) {
        setToast({ open: true, message: `Transcribing ${pendingRecs.length} audio recording${pendingRecs.length !== 1 ? 's' : ''}...`, severity: 'info' });
        try {
          const transcriptionResults = await transcribeAllRecordings();
          await handleTranscriptionComplete(transcriptionResults, { showNoAudioToast: false, showSuccessToast: true });
        } catch (err) {
          console.error('Transcription during sync failed:', err);
          setToast({ open: true, message: 'Some recordings could not be transcribed. Will retry on next sync.', severity: 'warning' });
        }
        getUntranscribedRecordings().then((all) => setPendingAudioCount(all.length));
      }

      // 2. Push pending evaluation drafts to the server
      const results = await processSyncQueue();
      const remaining = getPendingSyncCount();
      setPendingSyncCount(remaining);
      const totalSynced = (results.synced || 0) + (results.prospectsSynced || 0);
      const totalFailed = (results.failed || 0) + (results.prospectsFailed || 0);

      // If the draft currently loaded in the form was one of the items that just
      // synced, its on-screen copy is now a stale duplicate of the record that
      // lives on the server. Reset the form so a subsequent save/submit does not
      // create a second record for the same evaluation.
      const syncedDraftKeys = Array.isArray(results.syncedDraftKeys) ? results.syncedDraftKeys : [];
      const activeDraftKey = loadedDraftKeyRef.current;
      const currentDraftWasSynced = activeDraftKey && syncedDraftKeys.includes(activeDraftKey);

      if (totalSynced > 0 && totalFailed === 0) {
        setToast({ open: true, message: `${totalSynced} item${totalSynced !== 1 ? 's' : ''} synced successfully!`, severity: 'success' });
      } else if (totalFailed > 0) {
        setToast({ open: true, message: `${totalFailed} item${totalFailed !== 1 ? 's' : ''} failed to sync. Will retry on next save.`, severity: 'error' });
      }

      if (currentDraftWasSynced) {
        clearLocalDraftArtifacts();
        handleReset();
        setToast({ open: true, message: 'Your evaluation was synced and saved on the server. The form has been cleared to prevent a duplicate entry.', severity: 'info' });
      }
    } finally {
      setIsSyncingData(false);
    }
  }, [handleTranscriptionComplete, isSyncingData, clearLocalDraftArtifacts]);

  const syncPendingWorkBeforeSave = useCallback(async (baseForm, submitStatus) => {
    const pendingSyncItems = getPendingSyncCount();
    const pendingRecordings = await getUntranscribedRecordings();

    if (pendingSyncItems === 0 && pendingRecordings.length === 0) {
      return baseForm;
    }

    setIsSyncingData(true);
    setStatus({ message: 'Syncing pending offline data before saving...', type: 'info' });
    setToast({
      open: true,
      message: submitStatus === 'Completed'
        ? 'Syncing pending offline items before submitting your evaluation.'
        : 'Syncing pending offline items before saving your draft.',
      severity: 'info',
    });

    try {
      let nextForm = baseForm;
      let transcribeResults = [];

      if (pendingRecordings.length > 0) {
        try {
          transcribeResults = await transcribeAllRecordings();
        } catch (error) {
          console.error('Automatic transcription run failed:', error);
          transcribeResults = pendingRecordings.map((rec) => ({
            recordId: rec.id,
            fieldName: rec.fieldName,
            draftKey: rec.draftKey,
            transcription: null,
            error: error.message,
          }));
        }

        if (transcribeResults.length > 0) {
          nextForm = await handleTranscriptionComplete(transcribeResults, {
            baseForm: nextForm,
            showNoAudioToast: false,
            showSuccessToast: false,
          });
        }
      }

      const syncResults = await processSyncQueue();
      setPendingSyncCount(getPendingSyncCount());
      setPendingAudioCount((await getUntranscribedRecordings()).length);

      if (syncResults.failed > 0 || transcribeResults.some((result) => result.error)) {
        const action = submitStatus === 'Completed' ? 'submit' : 'save';
        setToast({
          open: true,
          message: `Some pending offline items could not sync. Continuing with your ${action}.`,
          severity: 'warning',
        });
      }

      setStatus({ message: '', type: '' });
      return nextForm;
    } finally {
      setIsSyncingData(false);
    }
  }, [handleTranscriptionComplete]);

  const handleReset = () => {
    if (isListening) handleStopAll();
    latestDraftPromptedRef.current = true;
    setForm(initialFormState);
    setSelectedEvent(null);
    setSelectedProspectUID('');
    setSelectedCompetencies({});
    setSelectedTangibles([]);
    setStatus({ message: '', type: '' });
    setAudioRecordingsMap({});
    setDraftKey(null);
    setLastAutoSave(null);
    setShowDraftDialog(false);
    setExistingDraftData(null);
    loadedDraftKeyRef.current = null;
  };

  const handleSubmitClick = () => {
    if (!navigator.onLine || isOfflineMode) {
      setShowOfflineSubmitDialog(true);
      return;
    }
    handleSave('Completed');
  };

  const handleConfirmOfflineSubmit = async () => {
    setShowOfflineSubmitDialog(false);
    await handleSave('Completed', { resetAfterQueue: true });
  };

  // ── Handle Draft Loading ──
  const handleLoadDraft = useCallback(() => {
    if (!existingDraftData) return;

    draftDetectionSkippedRef.current = existingDraftData._draftKey || false;
    loadedDraftKeyRef.current = existingDraftData._draftKey || loadedDraftKeyRef.current;

    // Hydrate form from draft
    if (existingDraftData.form) {
      setForm(existingDraftData.form);
    }
    if (existingDraftData.selectedEvent) {
      const matchingEvent = (evaluationEvents || []).find((eventItem) => eventItem.id === existingDraftData.selectedEvent.id);
      setSelectedEvent(matchingEvent || existingDraftData.selectedEvent);
    }
    if (existingDraftData.selectedProspectUID) {
      setSelectedProspectUID(existingDraftData.selectedProspectUID);
    }
    if (existingDraftData.selectedCompetencies) {
      setSelectedCompetencies(existingDraftData.selectedCompetencies);
    }
    if (existingDraftData.selectedTangibles) {
      setSelectedTangibles(existingDraftData.selectedTangibles);
    }
    if (existingDraftData._draftKey) {
      setDraftKey(existingDraftData._draftKey);
    }

    setShowDraftDialog(false);
    setExistingDraftData(null);
    setToast({
      open: true,
      message: `Draft for ${draftProspectName} loaded successfully!`,
      severity: 'success',
    });
  }, [existingDraftData, draftProspectName, evaluationEvents]);

  const handleStartFresh = useCallback(() => {
    draftDetectionSkippedRef.current = existingDraftData?._draftKey || draftDetectionSkippedRef.current;
    setShowDraftDialog(false);
    setExistingDraftData(null);
    // Don't reset form here - user is intentionally starting fresh
  }, [existingDraftData]);

  const ratingLabel = (val) => {
    const labels = { 1: 'Needs Improvement', 2: 'Below Expectations', 3: 'Meets Expectations', 4: 'Above Expectations', 5: 'Exceptional' };
    return labels[val] || '';
  };

  const openSummarizationReview = useCallback((config) => {
    setPendingSummarization(config);
    setShowSummarizationDialog(true);
  }, []);

  const handleSummarize = async () => {
    const currentSummary = form.evaluationSummary || '';

    setIsSummarizing(true);
    setStatus({ message: 'Generating AI summary...', type: 'info' });

    try {
      // Build dynamic categories payload from evaluationCategories (fetched from DB)
      const categories = (evaluationCategories || []).filter(cat => cat.categoryActive).map(cat => {
        const activeSubCategories = (cat.subCategories || []).filter(sub => sub.subCategoryActive);
        const subCategoryRatings = activeSubCategories.length > 0
          ? activeSubCategories.map(sub => ({
            subCategoryID: sub.id,
            rating: form[`sub_${sub.id}`] || 0,
          }))
          : [{ subCategoryID: 0, rating: form[`cat_${cat.id}`] || 0 }];

        // Gather competency names for this category
        const catCompNames = [];
        if (activeSubCategories.length > 0) {
          activeSubCategories.forEach(sub => {
            const subKey = `sub_comp_${sub.id}`;
            (selectedCompetencies[subKey] || []).forEach(cId => {
              const comp = competencies.find(c => c.id === cId);
              if (comp) catCompNames.push(comp.competencyName);
            });
          });
        } else {
          const catKey = `cat_comp_${cat.id}`;
          (selectedCompetencies[catKey] || []).forEach(cId => {
            const comp = competencies.find(c => c.id === cId);
            if (comp) catCompNames.push(comp.competencyName);
          });
        }

        return {
          categoryID: cat.id,
          notes: form[`category_${cat.id}`] || '',
          subCategoryRatings,
          competencies: catCompNames,
        };
      });

      // Gather all selected competency names
      const allCompetencyNames = [];
      Object.values(selectedCompetencies).forEach(ids => {
        (ids || []).forEach(cId => {
          const comp = competencies.find(c => c.id === cId);
          if (comp && !allCompetencyNames.includes(comp.competencyName)) {
            allCompetencyNames.push(comp.competencyName);
          }
        });
      });

      // Gather selected tangible names
      const selectedTangibleNames = (tangibles || [])
        .filter(t => selectedTangibles.includes(t.id))
        .map(t => t.name);

      const result = await EvaluationCategoryData.getSummarizedEvaluation({
        refereeName: form.refereeName,
        evaluationDate: form.evaluationDate,
        systemPrompt: systemPrompt,
        categories,
        competencies: allCompetencyNames,
        tangibles: selectedTangibleNames,
      });

      if (result) {
        openSummarizationReview({
          fieldName: 'evaluationSummary',
          dialogTitle: 'Review Evaluation Summary',
          sourceLabel: 'Current Summary',
          summarizedText: result,
          originalText: currentSummary,
          successMessage: 'Evaluation summary updated with AI response.',
          discardMessage: 'AI summary was discarded. Existing evaluation summary retained.',
        });
      }
      setStatus({ message: '', type: '' });
      setToast({ open: true, message: 'AI summary ready for review.', severity: 'info' });
    } catch (error) {
      console.warn('AI summary failed, falling back to local summary:', error);
      const fallbackSummary = generateLocalSummary();
      openSummarizationReview({
        fieldName: 'evaluationSummary',
        dialogTitle: 'Review Evaluation Summary',
        sourceLabel: 'Current Summary',
        summarizedText: fallbackSummary,
        originalText: currentSummary,
        successMessage: 'Evaluation summary updated with generated summary.',
        discardMessage: 'Generated summary was discarded. Existing evaluation summary retained.',
      });
      setStatus({ message: 'AI unavailable — review the generated local summary before applying it.', type: 'warning' });
      setToast({ open: true, message: 'Generated summary ready for review.', severity: 'info' });
    } finally {
      setIsSummarizing(false);
    }
  };

  const generateLocalSummary = () => {
    const activeCategories = (evaluationCategories || []).filter(cat => cat.categoryActive);

    // Build category scores dynamically from DB categories
    const categoryScores = activeCategories.map(cat => {
      const activeSubCategories = (cat.subCategories || []).filter(sub => sub.subCategoryActive);
      const ratings = activeSubCategories.length > 0
        ? activeSubCategories.map(sub => form[`sub_${sub.id}`] || 0)
        : [form[`cat_${cat.id}`] || 0];
      return { name: cat.categoryName, ratings };
    });

    const allRatings = categoryScores.flatMap(c => c.ratings);
    const avg = allRatings.length > 0
      ? (allRatings.reduce((a, b) => a + b, 0) / allRatings.length).toFixed(1)
      : '0.0';

    const strengths = categoryScores.filter(c => {
      const catAvg = c.ratings.reduce((a, b) => a + b, 0) / c.ratings.length;
      return catAvg >= 4;
    }).map(c => c.name);

    const improvements = categoryScores.filter(c => {
      const catAvg = c.ratings.reduce((a, b) => a + b, 0) / c.ratings.length;
      return catAvg <= 2;
    }).map(c => c.name);

    const parts = [];
    parts.push(`Overall evaluation score: ${avg}/5.0 (${ratingLabel(Math.round(parseFloat(avg)))}).`);

    if (strengths.length > 0) {
      parts.push(`Key strengths: ${strengths.join(', ')}.`);
    }
    if (improvements.length > 0) {
      parts.push(`Areas for improvement: ${improvements.join(', ')}.`);
    }

    const noteSummaries = activeCategories
      .map(cat => ({ label: cat.categoryName, note: form[`category_${cat.id}`] || '' }))
      .filter(n => n.note.trim())
      .map(n => `${n.label}: ${n.note.trim().substring(0, 150)}${n.note.trim().length > 150 ? '...' : ''}`);

    if (noteSummaries.length > 0) {
      parts.push('\n' + noteSummaries.join('\n'));
    }

    // Include selected competencies
    const allCompNames = [];
    Object.values(selectedCompetencies).forEach(ids => {
      (ids || []).forEach(cId => {
        const comp = competencies.find(c => c.id === cId);
        if (comp && !allCompNames.includes(comp.competencyName)) {
          allCompNames.push(comp.competencyName);
        }
      });
    });
    if (allCompNames.length > 0) {
      parts.push(`\nCompetencies: ${allCompNames.join(', ')}.`);
    }

    // Include selected tangibles
    const selTangibleNames = (tangibles || [])
      .filter(t => selectedTangibles.includes(t.id))
      .map(t => t.name);
    if (selTangibleNames.length > 0) {
      parts.push(`InTangibles: ${selTangibleNames.join(', ')}.`);
    }

    return parts.join(' ');
  };

  const handleSummarizeCategory = async (categoryId, categoryName) => {
    const currentNotes = form[`category_${categoryId}`] || '';

    if (!currentNotes.trim()) {
      setToast({ open: true, message: 'No notes to summarize. Please add notes first.', severity: 'info' });
      return;
    }

    setSummarizingCategoryId(categoryId);
    setToast({ open: true, message: `Summarizing ${categoryName}...`, severity: 'info' });

    try {
      const result = await EvaluationCategoryData.summarizeCategoryNotes(
        categoryName,
        currentNotes
      );

      if (result) {
        openSummarizationReview({
          fieldName: `category_${categoryId}`,
          dialogTitle: `Review ${categoryName} Summary`,
          sourceLabel: 'Original Notes',
          summarizedText: result,
          originalText: currentNotes,
          successMessage: `${categoryName} notes updated with summarized text.`,
          discardMessage: `${categoryName} summary was discarded. Original text retained.`,
        });
        setToast({ open: true, message: `${categoryName} summary ready for review.`, severity: 'info' });
      }
    } catch (error) {
      console.warn(`Category summarization failed for ${categoryName}:`, error);
      setToast({ open: true, message: `Failed to summarize ${categoryName}. Please try again.`, severity: 'error' });
    } finally {
      setSummarizingCategoryId(null);
    }
  };

  const closeSummarizationDialog = useCallback(() => {
    setShowSummarizationDialog(false);
    setPendingSummarization(null);
  }, []);

  const handleAcceptSummarization = () => {
    if (!pendingSummarization) return;

    const { fieldName, summarizedText, successMessage } = pendingSummarization;
    updateField(fieldName, summarizedText);
    setToast({
      open: true,
      message: successMessage,
      severity: 'success',
    });

    closeSummarizationDialog();
  };

  const handleRejectSummarization = () => {
    if (!pendingSummarization) return;

    const { discardMessage } = pendingSummarization;
    setToast({
      open: true,
      message: discardMessage,
      severity: 'info',
    });

    closeSummarizationDialog();
  };


  // Determine if enough data is present to enable AI Summary, Email, Report
  const hasAnyRating = (evaluationCategories || []).filter(c => c.categoryActive).some(cat => {
    const subs = (cat.subCategories || []).filter(s => s.subCategoryActive);
    return subs.length > 0
      ? subs.some(s => (form[`sub_${s.id}`] || 0) > 0)
      : (form[`cat_${cat.id}`] || 0) > 0;
  });
  const hasAnyCompetency = Object.values(selectedCompetencies).some(ids => ids && ids.length > 0);
  const hasAnyTangible = selectedTangibles.length > 0;
  const canGenerateReport =
    !!selectedEvent &&
    !!form.evaluationDate &&
    !!form.refereeName.trim() &&
    (hasAnyRating || hasAnyCompetency || hasAnyTangible);

  const isReadOnly = form.status === 'Submitted';

  const locationParts = [
    selectedEvent?.stateName || selectedEvent?.stateOther || '',
    selectedEvent?.city || '',
  ].filter(Boolean);

  const locationStr = useMemo(() => {
    let address = []
    if (locationParts.length > 0) {

      if (selectedEvent?.city) {
        address.push(`${selectedEvent.city}`)
      }
      if (selectedEvent.stateName || selectedEvent.stateOther) {
        address.push(`${selectedEvent.stateName || selectedEvent.stateOther || ''}`)
      }
      if (selectedEvent?.countryName) {
        address.push(`${selectedEvent.countryName}`)
      }

      return address.join(', ');
    }
    return '';
  }, [locationParts, selectedEvent]);

  return (
    <div>
      <Loader isLoading={isCategoriesLoading || isSaving || isSyncingData} />
      <AppBar position="fixed" className={classes.appbarBackground}>
        <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" className={classes.title}>
            Prospect Evaluation
          </Typography>
          <Box display="flex" alignItems="center" gridGap={8}>
            <Hidden xsDown>
              <Button
                onClick={() => history.push(RouteConstants.ADMIN_EVALUATION_SEARCH)}
                disabled={isOfflineMode}
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                style={{ textTransform: 'none' }}
              >
                SEARCH
              </Button>
              <Button
                onClick={() => history.push(RouteConstants.ADMIN_MAP_OFFICIALS)}
                disabled={isOfflineMode}
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                style={{ textTransform: 'none' }}
              >
                MAP OFFICIAL
              </Button>
              <Button
                onClick={() => setShowEmailModal(true)}
                disabled={!canGenerateReport || isOfflineMode}
                variant="contained"
                size="small"
                style={{ backgroundColor: !canGenerateReport || isOfflineMode ? '#bdbdbd' : '#059669', color: '#fff', textTransform: 'none' }}
                startIcon={<MailOutlineIcon />}
              >
                EMAIL
              </Button>
              <Button
                onClick={() => setShowReport(true)}
                disabled={!canGenerateReport || isOfflineMode}
                variant="contained"
                size="small"
                style={{ backgroundColor: !canGenerateReport || isOfflineMode ? '#bdbdbd' : '#1d428a', color: '#fff', textTransform: 'none' }}
                startIcon={<DescriptionIcon />}
              >
                REPORT
              </Button>
              <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
              <Button
                onClick={() => handleSave('Draft')}
                disabled={isReadOnly || isSaving}
                variant="outlined"
                size="small"
                startIcon={<SaveIcon />}
                style={{ textTransform: 'none' }}
              >
                SAVE DRAFT
              </Button>
              <Button
                onClick={handleSubmitClick}
                disabled={isSaving}
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SendIcon />}
                style={{ textTransform: 'none' }}
              >
                SUBMIT
              </Button>
              <Button
                onClick={handleReset}
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<RefreshIcon />}
                style={{ textTransform: 'none' }}
              >
                RESET
              </Button>
            </Hidden>
            <Hidden smUp>
              <Tooltip title="Search">
                <IconButton size="small" onClick={() => history.push(RouteConstants.ADMIN_EVALUATION_SEARCH)} disabled={isOfflineMode} color="default">
                  <SearchIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Map Official">
                <span>
                  <IconButton size="small" onClick={() => history.push(RouteConstants.ADMIN_MAP_OFFICIALS)} disabled={isOfflineMode} color="default">
                    <LinkIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Email">
                <span>
                  <IconButton size="small" onClick={() => setShowEmailModal(true)} disabled={!canGenerateReport || isOfflineMode} style={{ color: '#059669' }}>
                    <MailOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Report">
                <span>
                  <IconButton size="small" onClick={() => setShowReport(true)} disabled={!canGenerateReport || isOfflineMode} style={{ color: '#1d428a' }}>
                    <DescriptionIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Save Draft">
                <span>
                  <IconButton size="small" onClick={() => handleSave('Draft')} disabled={isReadOnly || isSaving} color="default">
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Submit">
                <span>
                  <IconButton size="small" onClick={handleSubmitClick} disabled={isSaving} color="primary">
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset">
                <IconButton size="small" onClick={handleReset} color="secondary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Hidden>
          </Box>
        </Toolbar>
      </AppBar>

      <Grid container className={classes.content}>
        <Grid item xs={12}>
          {/* Status Bar */}
          {status.message && (
            <StatusBar
              message={status.message}
              type={status.type}
              onClose={() => setStatus({ message: '', type: '' })}
            />
          )}

          {/* Network Status Bar */}
          {(pendingSyncCount > 0 || isOfflineMode) && (
            <Box mb={2}>
              <NetworkStatusBar
                pendingSyncCount={pendingSyncCount}
                pendingAudioCount={pendingAudioCount}
                lastAutoSave={lastAutoSave}
                isOfflineMode={isOfflineMode}
                isSyncing={isSyncingData}
                onSyncClick={handleSyncPending}
              />
            </Box>
          )}

          {/* Basic Info Section */}
          <Paper variant="outlined" style={{ padding: 24, marginBottom: 24 }}>
            <Grid container spacing={2}>
              {/* Evaluation Event */}
              <Grid item xs={12} md={5}>
                <Box display="flex" alignItems="center" justifyContent="end" mb={1}>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={isScoutingCategoriesLoading ? <CircularProgress size={14} /> : <AddIcon style={{ fontSize: 16 }} />}
                    onClick={() => setOpenAddEvent(true)}
                    disabled={isScoutingCategoriesLoading}
                    style={{ textTransform: 'none', fontSize: '0.75rem', padding: '2px 8px' }}
                  >
                    New Event
                  </Button>
                </Box>
                <Autocomplete
                  value={selectedEvent}
                  options={evaluationEvents || []}
                  getOptionLabel={(option) => {
                    if (!option.eventName) return '';
                    const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
                    const from = fmtDate(option.eventFrom);
                    const to = fmtDate(option.eventTo);
                    const dateRange = from ? ` (${from}${to ? ` — ${to}` : ''})` : '';
                    return `${option.eventName}${dateRange}`;
                  }}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(e, newValue) => setSelectedEvent(newValue)}
                  size="small"
                  variant="outlined"
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      required
                      label="Evaluation Event"
                      placeholder="Select an evaluation event..."
                    />
                  )}
                />
              </Grid>
              {/* Prospect Name */}
              <Grid item xs={12} md={5}>
                <Box display="flex" alignItems="center" justifyContent="end" mb={1}>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<AddIcon style={{ fontSize: 16 }} />}
                    onClick={() => setOpenAddProspect(true)}
                    disabled={isReadOnly}
                    style={{ textTransform: 'none', fontSize: '0.75rem', padding: '2px 8px' }}
                  >
                    New Prospect
                  </Button>
                </Box>
                <ProspectTypeahead
                  label="Prospect Name"
                  required
                  value={form.refereeName}
                  onChange={(val, prospect) => {
                    updateField('refereeName', val);
                    if (prospect) setSelectedProspectUID(prospect.id);
                    else if (!val) setSelectedProspectUID('');
                  }}
                  onSearch={isReadOnly ? undefined : searchProspects}
                  placeholder="Search or type prospect name..."
                  prospects={evalUserList}
                  disabled={isReadOnly}
                />
              </Grid>
              {/* Date */}
              <Grid item xs={6} md={2} lg={2}>
                <Box mb={1} style={{ height: 25 }} />
                <TextField
                  type="date"
                  variant="outlined"
                  required
                  label="Review Date"
                  size="small"
                  className={classes.dateField}
                  value={form.evaluationDate}
                  onChange={(e) => updateField('evaluationDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: {
                      style: { cursor: 'pointer' },
                    },
                    style: { cursor: 'pointer' },
                  }}
                  style={{ maxWidth: 180 }}
                />
              </Grid>
            </Grid>
            {selectedEvent && (
              <Box mt={1} p={1} style={{ backgroundColor: '#f8fafc', borderRadius: 6, fontSize: '0.78rem', color: '#475569' }}>
                <Typography variant="caption" style={{ display: 'block', color: '#555' }}>
                  <strong>Address:</strong> {locationStr}
                </Typography>
                {/* {selectedEvent.stateName || selectedEvent.stateOther ? (
              <Typography variant="caption" style={{ display: 'block', color: '#555' }}>
                <strong>State:</strong> {selectedEvent.stateName || selectedEvent.stateOther}
                {selectedEvent.city ? <>, <strong>City:</strong> {selectedEvent.city}</> : ''}
                {selectedEvent.countryName ? <>, <strong>Country:</strong> {selectedEvent.countryName}</> : ''}
              </Typography>
            ) : selectedEvent.countryName ? (
              <Typography variant="caption" style={{ display: 'block', color: '#555' }}>
                {selectedEvent.city ? <><strong>City:</strong> {selectedEvent.city}, </> : ''}
                <strong>Country:</strong> {selectedEvent.countryName}
              </Typography>
            ) : null}*/}
                {selectedEvent.gameTypeName && selectedEvent.gameTypeName !== '' && selectedEvent.scoutingCategoryName==null && selectedEvent.scoutingSubCategoryName==null ? (
                  <Typography variant="caption" style={{ display: 'block', color: '#555' }}>
                    <strong>Game Type:</strong> {selectedEvent.gameTypeName}
                  </Typography>
                ) : 
                <Box display="flex" alignItems="center" style={{ color: '#555' }}>
                  <Typography variant="caption" style={{ display: 'block' }}>
                    <strong>Category Name:</strong> {selectedEvent.scoutingCategoryName || 'N/A'}
                  </Typography>
                  <Typography variant="caption" style={{ display: 'block', marginLeft: 12 }}>
                    <strong>Sub Category:</strong> {selectedEvent.scoutingSubCategoryName || 'N/A'}
                  </Typography>
                </Box>
                }
                
               </Box> )}
          </Paper>
        </Grid>

        {/* Category Sections */}
        {evaluationCategories && evaluationCategories.filter((cat) => cat.categoryActive).map((cat) => {
          const activeSubCategories = (cat.subCategories || []).filter((sub) => sub.subCategoryActive);
          return (
            <Paper key={cat.id} variant="outlined" style={{ padding: 24, marginBottom: 16, width: '100%' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12, textTransform: 'uppercase' }}>
                {cat.categoryName}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {activeSubCategories.length > 0 ? (
                    <Box mb={2} pb={2} borderBottom={1} borderColor="divider">
                      <Grid container spacing={2}>
                        {activeSubCategories.map((sub) => {
                          const subComps = competencies.filter((c) => c.subCategoryID === sub.id);
                          const subKey = `sub_comp_${sub.id}`;
                          return (
                            <Grid item xs={12} sm={6} md={4} key={sub.id}>
                              <ScoreSlider
                                label={sub.subCategoryName}
                                value={form[`sub_${sub.id}`] || 0}
                                onChange={(val) => updateField(`sub_${sub.id}`, val)}
                                maxScore={5}
                              />
                              {subComps.length > 0 && (
                                <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                  {subComps.map((comp) => (
                                    <Chip
                                      key={comp.id}
                                      label={comp.competencyName}
                                      size="small"
                                      clickable
                                      color={(selectedCompetencies[subKey] || []).includes(comp.id) ? 'primary' : 'default'}
                                      style={!(selectedCompetencies[subKey] || []).includes(comp.id) ? { backgroundColor: '#e0e0e0' } : {}}
                                      onClick={() => toggleCompetency(subKey, comp.id)}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Box mb={2} pb={2} borderBottom={1} borderColor="divider">
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={6}>
                          <ScoreSlider
                            label=""
                            value={form[`cat_${cat.id}`] || 0}
                            onChange={(val) => updateField(`cat_${cat.id}`, val)}
                            maxScore={5}
                          />
                          {(() => {
                            const catComps = competencies.filter((c) => c.categoryID === cat.id);
                            const catKey = `cat_comp_${cat.id}`;
                            return catComps.length > 0 ? (
                              <Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                {catComps.map((comp) => (
                                  <Chip
                                    key={comp.id}
                                    label={comp.competencyName}
                                    size="small"
                                    clickable
                                    color={(selectedCompetencies[catKey] || []).includes(comp.id) ? 'primary' : 'default'}
                                    style={!(selectedCompetencies[catKey] || []).includes(comp.id) ? { backgroundColor: '#e0e0e0' } : {}}
                                    onClick={() => toggleCompetency(catKey, comp.id)}
                                  />
                                ))}
                              </Box>
                            ) : null
                          })()}
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                </Grid>
                <Grid item xs={12}>
                  <SpeechTextArea
                    label={`${cat.categoryName} Notes`}
                    fieldName={`category_${cat.id}`}
                    value={form[`category_${cat.id}`] || ''}
                    onChange={updateField}
                    onMicClick={handleMicClick}
                    isListening={isListening}
                    isLoading={isLoading}
                    activeField={activeField}
                    interimText={interimText}
                    placeholder={`Dictate your evaluation for ${cat.categoryName}...`}
                    isOfflineMode={isOfflineMode}
                    audioRecordings={audioRecordingsMap[`category_${cat.id}`] || []}
                    onRecordingComplete={handleRecordingComplete}
                    onDeleteRecording={handleDeleteRecording}
                    actionButton={
                      <>
                        <Hidden xsDown>
                          <Button
                            onClick={() => handleSummarizeCategory(cat.id, cat.categoryName)}
                            disabled={summarizingCategoryId === cat.id || !form[`category_${cat.id}`]?.trim()}
                            variant="contained"
                            size="small"
                            style={{ backgroundColor: (summarizingCategoryId === cat.id || !form[`category_${cat.id}`]?.trim()) ? '#bdbdbd' : '#7c3aed', color: '#fff', textTransform: 'none' }}
                            startIcon={<SparklesIcon className={summarizingCategoryId === cat.id ? 'spin-icon' : ''} />}
                          >
                            {summarizingCategoryId === cat.id ? 'Summarizing...' : 'Summarize'}
                          </Button>
                        </Hidden>
                        <Hidden smUp>
                          <IconButton
                            onClick={() => handleSummarizeCategory(cat.id, cat.categoryName)}
                            disabled={summarizingCategoryId === cat.id || !form[`category_${cat.id}`]?.trim()}
                            size="small"
                            style={{ color: (summarizingCategoryId === cat.id || !form[`category_${cat.id}`]?.trim()) ? '#bdbdbd' : '#7c3aed' }}
                            title={`Summarize ${cat.categoryName}`}
                          >
                            <SparklesIcon className={summarizingCategoryId === cat.id ? 'spin-icon' : ''} />
                          </IconButton>
                        </Hidden>
                      </>
                    }
                  />
                </Grid>
              </Grid>
            </Paper>
          );
        })}

        {/* Hidden System Prompt */}
        <div style={{ display: 'none' }}>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            id="systemPrompt"
            name="systemPrompt"
          />
        </div>

        {/* Evaluation Summary */}
        <Grid item xs={12} spacing={2}>
          <Paper variant="outlined" style={{ padding: 24, marginBottom: 24 }}>
            <SpeechTextArea
              label="EVALUATION SUMMARY"
              fieldName="evaluationSummary"
              value={form.evaluationSummary}
              onChange={updateField}
              onMicClick={handleMicClick}
              isListening={isListening}
              isLoading={isLoading}
              activeField={activeField}
              interimText={interimText}
              placeholder="Dictate your overall evaluation summary — key strengths, areas for improvement, and recommendations..."
              rows={8}
              isOfflineMode={isOfflineMode}
              audioRecordings={audioRecordingsMap['evaluationSummary'] || []}
              onRecordingComplete={handleRecordingComplete}
              onDeleteRecording={handleDeleteRecording}
              actionButton={
                <>
                  <Hidden xsDown>
                    <Button
                      onClick={handleSummarize}
                      disabled={isSummarizing || !canGenerateReport || isOfflineMode}
                      variant="contained"
                      size="small"
                      style={{ backgroundColor: (isSummarizing || !canGenerateReport || isOfflineMode) ? '#bdbdbd' : '#7c3aed', color: '#fff', textTransform: 'none' }}
                      startIcon={<SparklesIcon className={isSummarizing ? 'spin-icon' : ''} />}
                    >
                      {isSummarizing ? 'Summarizing...' : 'AI Summary'}
                    </Button>
                  </Hidden>
                  <Hidden smUp>
                    <IconButton
                      onClick={handleSummarize}
                      disabled={isSummarizing || !canGenerateReport || isOfflineMode}
                      size="small"
                      style={{ color: (isSummarizing || !canGenerateReport || isOfflineMode) ? '#bdbdbd' : '#7c3aed' }}
                    >
                      <SparklesIcon className={isSummarizing ? 'spin-icon' : ''} />
                    </IconButton>
                  </Hidden>
                </>
              }
            />

            {tangibles.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                  INTANGIBLES
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  {tangibles.map((tangible) => (
                    <Chip
                      key={tangible.id}
                      label={tangible.name}
                      size="small"
                      clickable
                      color={selectedTangibles.includes(tangible.id) ? 'primary' : 'default'}
                      variant={selectedTangibles.includes(tangible.id) ? 'default' : 'outlined'}
                      onClick={() => toggleTangible(tangible.id)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Email Modal */}
        {showEmailModal && (
          <EmailModal
            form={form}
            evaluationCategories={evaluationCategories}
            tangibles={tangibles}
            selectedTangibles={selectedTangibles}
            competencies={competencies}
            selectedCompetencies={selectedCompetencies}
            selectedEvent={selectedEvent}
            userEmail={user?.email || ''}
            onClose={() => setShowEmailModal(false)}
          />
        )}

        {/* Summarization Confirmation Dialog */}
        {pendingSummarization && (
          <Dialog
            open={showSummarizationDialog}
            onClose={closeSummarizationDialog}
            disableBackdropClick
            maxWidth="md"
            fullWidth
          >
            <DialogTitle disableTypography style={{ position: 'relative', paddingRight: 48 }}>
              <Typography variant="h6">{pendingSummarization.dialogTitle}</Typography>
              <IconButton
                size="small"
                onClick={closeSummarizationDialog}
                aria-label="Close summary review dialog"
                style={{ position: 'absolute', top: 8, right: 8 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box style={{ marginTop: 16 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                  {pendingSummarization.sourceLabel}:
                </Typography>
                <Paper style={{ padding: 12, backgroundColor: '#f5f5f5', marginBottom: 16 }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {pendingSummarization.originalText || 'No existing text.'}
                  </Typography>
                </Paper>

                <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8, marginTop: 16 }}>
                  AI Generated Summary:
                </Typography>
                <Paper style={{ padding: 12, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {pendingSummarization.summarizedText}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRejectSummarization} color="default">
                Discard
              </Button>
              <Button onClick={handleAcceptSummarization} color="primary" variant="contained">
                Accept & Update
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Evaluation Report */}
        <EvaluationReport
          open={showReport}
          onClose={() => setShowReport(false)}
          form={form}
          evaluationCategories={evaluationCategories}
          competencies={competencies}
          selectedCompetencies={selectedCompetencies}
          tangibles={tangibles}
          selectedTangibles={selectedTangibles}
          selectedEvent={selectedEvent}
          onSwitchToText={() => { setShowReport(false); setShowTextReport(true); }}
        />

        {/* Text Report */}
        <EvaluationTextReport
          open={showTextReport}
          onClose={() => setShowTextReport(false)}
          form={form}
          evaluationCategories={evaluationCategories}
          competencies={competencies}
          selectedCompetencies={selectedCompetencies}
          tangibles={tangibles}
          selectedTangibles={selectedTangibles}
          selectedEvent={selectedEvent}
          onSwitchToVisual={() => { setShowTextReport(false); setShowReport(true); }}
        />

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MuiAlert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            elevation={6}
            variant="filled"
          >
            {toast.message}
          </MuiAlert>
        </Snackbar>

        {/* Add New Event Modal */}
        {openAddEvent && (
          <AddNewEvent
            open={openAddEvent}
            scoutingCategoryList={scoutingCategoryList}
            handleCloseClick={() => setOpenAddEvent(false)}
            showAlert={(msg, severity) => addSnackMessages([{ open: true, severity, message: msg }])}
            handleEventAdded={(eventData) => {
              if (eventData?.eventName) pendingEventNameRef.current = eventData.eventName;
              getUpdatedEvaluationEvents();
            }}
            evaluationEvents={evaluationEvents}
            editEvent={{ id: 0, eventName: '', seasonCycleYear: '', seasonType: 0, seasonTypeName: '', isNBAEvent: true, eventFrom: null, eventTo: null, gameType: 0, country: 0, state: 0, stateOther: '', city: '', evaluationMode: '' }}
          />
        )}

        {/* Add New Prospect Modal */}
        <AddProspectModal
          open={openAddProspect}
          onClose={() => setOpenAddProspect(false)}
          onSave={handleProspectSave}
          isOfflineMode={isOfflineMode}
          onProspectAdded={(prospect) => {
            console.log('Prospect added response:', prospect);
            const uid = prospect.officialUID || prospect.OfficialUID || prospect.id || '';
            console.log('Resolved officialUID:', uid);
            const fullName = `${prospect.firstName || ''} ${prospect.lastName || ''}`.trim();
            const newProspect = {
              officialUID: uid,
              firstName: prospect.firstName || '',
              lastName: prospect.lastName || '',
              email: prospect.email || '',
            };
            setEvalUserList([newProspect]);
            updateField('refereeName', fullName);
            setSelectedProspectUID(uid);
          }}
        />

        {/* Sync Panel - OFFLINE SYNC DISABLED */}
        {/* <SyncPanel
        open={showSyncPanel}
        isOfflineMode={isOfflineMode}
        evaluationCategories={evaluationCategories}
        onClose={() => {
          setShowSyncPanel(false);
          setPendingSyncCount(getPendingSyncCount());
        }}
        onSyncComplete={(results) => {
          setPendingSyncCount(getPendingSyncCount());
        }}
        onTranscriptionComplete={handleTranscriptionComplete}
      /> */}

        {/* Bottom Action Buttons */}
        <Grid item xs={12}>
          {/* Desktop: full buttons */}
          <Hidden xsDown>
            <Box display="flex" justifyContent="flex-end" flexWrap="wrap" style={{ gap: 8, marginTop: 16 }}>
              <Button
                onClick={() => history.push(RouteConstants.ADMIN_EVALUATION_SEARCH)}
                disabled={isOfflineMode}
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                style={{ textTransform: 'none' }}
              >
                SEARCH
              </Button>
              <Button
                onClick={() => setShowEmailModal(true)}
                disabled={!canGenerateReport || isOfflineMode}
                variant="contained"
                size="small"
                style={{ backgroundColor: !canGenerateReport || isOfflineMode ? '#bdbdbd' : '#059669', color: '#fff', textTransform: 'none' }}
                startIcon={<MailOutlineIcon />}
              >
                EMAIL
              </Button>
              <Button
                onClick={() => setShowReport(true)}
                disabled={!canGenerateReport || isOfflineMode}
                variant="contained"
                size="small"
                style={{ backgroundColor: !canGenerateReport || isOfflineMode ? '#bdbdbd' : '#1d428a', color: '#fff', textTransform: 'none' }}
                startIcon={<DescriptionIcon />}
              >
                REPORT
              </Button>
              <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
              <Button
                onClick={() => handleSave('Draft')}
                disabled={isReadOnly || isSaving}
                variant="outlined"
                size="small"
                startIcon={<SaveIcon />}
                style={{ textTransform: 'none' }}
              >
                SAVE DRAFT
              </Button>
              <Button
                onClick={handleSubmitClick}
                disabled={isSaving}
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SendIcon />}
                style={{ textTransform: 'none' }}
              >
                SUBMIT
              </Button>
              <Button
                onClick={handleReset}
                disabled={isOfflineMode}
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<RefreshIcon />}
                style={{ textTransform: 'none' }}
              >
                RESET
              </Button>
            </Box>
          </Hidden>
          {/* Mobile: icon-only buttons with tooltips */}
          <Hidden smUp>
            <Box display="flex" justifyContent="flex-end" style={{ gap: 4, marginTop: 16 }}>
              <Tooltip title="Search">
                <span>
                  <IconButton size="small" onClick={() => history.push(RouteConstants.ADMIN_EVALUATION_SEARCH)} disabled={isOfflineMode} color="default">
                    <SearchIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Email">
                <span>
                  <IconButton size="small" onClick={() => setShowEmailModal(true)} disabled={!canGenerateReport || isOfflineMode} style={{ color: '#059669' }}>
                    <MailOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Report">
                <span>
                  <IconButton size="small" onClick={() => setShowReport(true)} disabled={!canGenerateReport || isOfflineMode} style={{ color: '#1d428a' }}>
                    <DescriptionIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Save Draft">
                <span>
                  <IconButton size="small" onClick={() => handleSave('Draft')} disabled={isReadOnly || isSaving} color="default">
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Submit">
                <span>
                  <IconButton size="small" onClick={handleSubmitClick} disabled={isSaving} color="primary">
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reset">
                <span>
                  <IconButton size="small" onClick={handleReset} disabled={isOfflineMode} color="secondary">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Hidden>
        </Grid>

        {/* Offline Submit Confirmation */}
        <Dialog open={showOfflineSubmitDialog} onClose={() => setShowOfflineSubmitDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Queue And Open New Form?
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 16, color: '#475569' }}>
              You are offline. This evaluation will be queued for sync, and a new blank evaluation form will open for the next prospect.
            </Typography>
            <Typography variant="body2" style={{ color: '#475569' }}>
              Continue only if you are done with this prospect and want to start a new evaluation.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowOfflineSubmitDialog(false)} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleConfirmOfflineSubmit} color="primary" variant="contained" autoFocus>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Draft Detection Dialog */}
        <Dialog open={showDraftDialog} onClose={handleStartFresh} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Found Saved Draft
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ marginBottom: 16, color: '#475569' }}>
              A saved draft for <strong>{draftProspectName}</strong> was found. Would you like to continue editing it or start fresh?
            </Typography>
            <Typography variant="caption" style={{ color: '#94a3b8' }}>
              {existingDraftData?.form?.evaluationDate && `Last saved: ${new Date(existingDraftData._savedAt || 0).toLocaleString()}`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleStartFresh} color="secondary" variant="outlined">
              Start Fresh
            </Button>
            <Button onClick={handleLoadDraft} color="primary" variant="contained" autoFocus>
              Load Draft
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </div>
  )
};

export default EvaluationForm;
